import { Capacitor } from "@capacitor/core";
import { Fragment, h } from "@stencil/core";
import { stepViewOptions } from "../../global/step-utils";
import { plannedWorkoutSlugs } from "../../global/workout-utils";
import { showOnlyCurriculumAccess } from "./sw-portal/portal-utils";
import { routerKeyByDataType } from "./sw-firestore-data/firestore-data-utils";
import { firestoreDataTypes } from "@sworkit/api";

export function renderRouter(
  authLoaded: boolean,
  isAuthenticated: boolean,
  loyalUser: boolean,
  sworkitAdmin: boolean,
  orgPermission: AuthState["orgPermission"],
  orgAdmin: AuthState["orgAdmin"], //todo: redo old permissions
  orgOwner: AuthState["orgOwner"],
  curriculumOnly: AuthState["curriculumOnly"],
  oAuthSessionInitiated: AuthState["oAuthInitiated"],
  _experiments: SworkitState["experiments"],
) {
  return (
    <ion-router useHash={false}>
      {/* todo: is this necessary? seems like the display routes are taken care of elsewhere */}
      {showOnlyCurriculumAccess(
        { curriculumOnly, orgAdmin, orgOwner },
        orgPermission,
      )
        ? getCurriculumOnlyPortalRoutes(isAuthenticated)
        : getPortalRoutes(isAuthenticated)}
      <ion-route component="sw-gym">
        <ion-route url="/favorites/" component="sw-favorites-page" />
        <ion-route
          url="/choose-challenge/:challengeBannerId"
          component="sw-choose-challenge-page"
        />
        <ion-route url="/studio-workouts" component="sw-studio-all-page" />
        <ion-route
          url="/studio-workouts/:collectionSlug"
          component="sw-studio-collection-page"
        />
        <ion-route
          url="/studio-workouts/:collectionSlug/:workoutSlug"
          component="sw-studio-page"
        />
        <ion-route
          url="/collections/:collectionSlug"
          component="sw-collection-page"
        />
        <ion-route
          url="/collections/:collectionSlug/:workoutSlug"
          component="sw-workout-page"
        />
        <ion-route
          url="/collections/:collectionSlug/:workoutSlug/time-select"
          component="sw-time-select-page"
        />
        <ion-route
          url="/collections/:collectionSlug/:workoutSlug/music-select"
          component="sw-music-playlist-page"
        />
        <ion-route
          url="/custom/:workoutSlug" // legacy route used for importing workouts
          component="sw-workout-page"
          componentProps={{
            collectionSlug: "custom",
          }}
        />
        <ion-route url="/reminders" component="sw-reminders-page" />
        <ion-route url="/settings" component="sw-settings-page" />
        <ion-route url="/settings/debug-info" component="sw-debug-info-page" />
        <ion-route url="/steps" component="sw-steps" />
        <ion-route
          url="/settings/notifications"
          component="sw-notifications-page"
        />
        <ion-route
          url="/settings/reminders"
          component="sw-reminders-page"
          componentProps={{
            defaultBackHref: "/settings",
          }}
        />
        <ion-route
          url="/settings/integrations/:integration/:status"
          component="sw-integration-page"
        />
        <ion-route
          url="/edit-workout-goals"
          component="sw-edit-workout-goals-page"
        />
        <ion-route
          url="/recommendation/primary"
          component="sw-onboarding-primary-page"
        />
        <ion-route
          url="/recommendation/secondary/:onboardingChoice"
          component="sw-onboarding-secondary-page"
        />
        <ion-route
          url="/recommendation/activity-level"
          component="sw-recommendation-activity-level-page"
        />
        <ion-route
          url="/recommendation/interest"
          component="sw-recommendation-interest-page"
        />
        <ion-route
          url="/recommendation/injuries"
          component="sw-recommendation-injuries-page"
        />
        <ion-route
          url="/recommendation/pain"
          component="sw-recommendation-pain-page"
        />
        <ion-route
          url="/recommendation/age"
          component="sw-recommendation-age-page"
        />
        <ion-route
          url="/recommendation/weight"
          component="sw-recommendation-weight-page"
        />
        <ion-route
          url="/recommendation/gender"
          component="sw-recommendation-gender-page"
        />
        <ion-route
          url="/recommendation/struggle"
          component="sw-recommendation-struggle-page"
        />
        <ion-route
          url="/recommendation/goal"
          component="sw-recommendation-goal-page"
        />
        <ion-route
          url="/recommendation/recommended-plan"
          component="sw-recommendation-collection-page"
        />
        {isAuthenticated
          ? routesThatDontRequireLogin.map((route) => (
              <ion-route url={route.url} component={route.component} />
            ))
          : null}
        <ion-route url="/help" component="sw-help-page" />
        <ion-route url="/workout-library" component="sw-workout-library-page" />
        <ion-route
          url="/workout-library/filter"
          component="sw-workout-filter-page"
        />
        <ion-route
          url="/my-sworkit/workouts-summary"
          component="sw-my-workouts-summary-page"
        />
        <ion-route url="/my-assignments" component="sw-user-assignments-page" />
        <ion-route component="sw-tabs">
          <ion-route url="/" component="workouts-tab" />

          <ion-route component="plans-tab">
            <ion-route url="/goals" component="sw-plans-goal-select-page" />
            <ion-route
              url="/goals/:goalSlug"
              component="sw-plans-level-select-page"
            />
            <ion-route
              url="plans/:collectionSlug"
              component="sw-plans-plan-select-page"
            />
            <ion-route
              url="plans/:collectionSlug/select"
              component="sw-plans-plan-select-page"
              componentProps={{ defaultBackHref: "/" }}
            />
          </ion-route>
          <ion-route url="/custom" component="custom-tab">
            <ion-route component="sw-custom-page" />
          </ion-route>
          <ion-route url="/my-sworkit" component="my-sworkit-tab">
            <ion-route url="/" component="sw-my-sworkit-page" />
            <ion-route url="/stats" component="sw-user-stats" />
            <ion-route
              url="/assessment-stats"
              component="sw-assessment-stats"
            />
            <ion-route url="/workout-log" component="sw-workout-log-page" />
            {stepViewOptions.map((viewOption) => (
              <ion-route
                url={`/${viewOption}`}
                component="sw-step-detail-page"
                componentProps={{
                  viewOption,
                }}
              />
            ))}
          </ion-route>
        </ion-route>
        {plannedWorkoutSlugs.map((slug) => (
          <ion-route
            url={`/collections/${slug}/:workoutSlug`}
            component="sw-workout-page"
            componentProps={{
              collectionSlug: slug,
            }}
          />
        ))}
        {sworkitAdmin
          ? [
              <ion-route url="/test-ads" component="sw-test-ads-page" />,
              <ion-route url="/view-promos" component="sw-view-promos-page" />,
              <ion-route
                url="/data-admin"
                component="sw-firestore-data-settings-page"
              />,


              //admin page for dataTypes
              [
                ...Object.keys(routerKeyByDataType).map((dataType) => (
                  <ion-route
                    url={
                      routerKeyByDataType[dataType].url ||
                      `${routerKeyByDataType[dataType].key}-admin`
                    }
                    component={`sw-${routerKeyByDataType[dataType].key}-admin-page`}
                  />
                )),
              ],
              //edit pages
              [
                ...Object.keys(routerKeyByDataType).map((dataType) => (
                  <ion-route
                    url={
                      (routerKeyByDataType[dataType].url ||
                        `${routerKeyByDataType[dataType].key}-admin`) + `/:slug`
                    }
                    component={`sw-edit-${routerKeyByDataType[dataType].key}`}
                  />
                )),
              ],

              [...firestoreDataTypes, "aiAudio", "uiTranslations"].map(

                (dataType) => (
                  <ion-route
                    url={
                      (routerKeyByDataType[dataType].url ||
                        `${routerKeyByDataType[dataType].key}-admin`) +
                      `/:slug/translations`
                    }
                    component="firestore-data-translation-edit"
                  />
                ),
              ),
              <ion-route
                url="/assessment-admin"
                component="sw-assessment-admin-page"
              />,
              <ion-route
                url="/assessment-admin/assessments"
                component="sw-assessment-admin-page"
                componentProps={{ activeSegment: "assessments" }}
              />,
              <ion-route
                url="/assessment-admin/exercises"
                component="sw-assessment-admin-page"
                componentProps={{ activeSegment: "assessmentExercises" }}
              />,
              // <ion-route
              //   url="/assessment-admin/exercises/:urlSlug"
              //   component="sw-edit-assessment-exercise"
              // />,
              <ion-route url="/ai-audio-admin" component="sw-ai-audio-admin" />,
              <ion-route
                url="/view-challenge-banners"
                component="sw-view-challenge-banners-page"
              />,
              <ion-route
                url="/view-challenges"
                component="sw-view-challenges-page"
              />,
              <ion-route
                url="/challenge-examples"
                component="sw-challenge-examples"
              />,
              <ion-route
                url="/test-recommendations"
                component="sw-test-recommended-collection-page"
              />,
              <ion-route
                url="/test-workout-recommendations"
                component="sw-test-recommended-workouts-page"
              />,
              <ion-route
                url="/test-billboards"
                component="sw-test-billboards-page"
              />,
            ]
          : null}
        <ion-route component="sw-subscribe-page" url="/subscribe" />
        {isAuthenticated
          ? routesThatRequireLogin.map((component) => (
              <ion-route url={component.url} component={component.component} />
            ))
          : null}
      </ion-route>
      {/* Routes above are part of gym - with side menu */}
      {/* Routes below are full screen */}
      {!authLoaded || isAuthenticated
        ? null
        : routesThatRequireLogin.map((component) => (
            <ion-route url={component.url} component="sw-login-page" />
          ))}
      {!authLoaded || isAuthenticated
        ? null
        : routesThatDontRequireLogin.map((route) => (
            <ion-route url={route.url} component={route.component} />
          ))}
      <ion-route url="/collections/custom" component="custom-tab">
        <ion-route component="sw-custom-page" />
      </ion-route>
      <ion-route
        url="/collections/:collectionSlug/:workoutSlug/meditation-player"
        component="sw-meditation-player-page"
      />
      <ion-route
        url="/studio-workouts/:collectionSlug/:workoutSlug/player"
        component="sw-studio-player-page"
      />
      <ion-route
        url="/collections/:collectionSlug/:workoutSlug/studio-player"
        component="sw-studio-player-page"
      />
      <ion-route
        url="/collections/:collectionSlug/:contentSlug/learning-library-player"
        component="sw-learning-library-player-page"
      />
      <ion-route
        url="/collections/:collectionSlug/:workoutSlug/end-meditation"
        component="sw-end-meditation-page"
      />
      <ion-route
        url="/collections/:collectionSlug/:workoutSlug/end-learning-library"
        component="sw-end-learning-library-page"
      />
      <ion-route
        url="/collections/:collectionSlug/:workoutSlug/player"
        component="sw-workout-player-page"
      />
      <ion-route url="/assessments" component="sw-assessments-page" />
      <ion-route
        url="/assessments/:assessmentSlug"
        component="sw-assessment-page"
      />
      <ion-route
        url="/assessments/:assessmentSlug/player"
        component="sw-assessment-player-page"
      />
      <ion-route
        url="/collections/:collectionSlug/:workoutSlug/player/:workoutLength"
        component="sw-workout-player-page"
      />
      <ion-route
        url="/collections/:collectionSlug/:workoutSlug/end-workout/:caloriesBurned/:minutesCompleted"
        component="sw-end-workout-page"
      />
      <ion-route url="/login" component="sw-login-page" />
      <ion-route url="/clever" component="sw-clever-login-page" />
      <ion-route url="/auth/fitbit" component="sw-fitbit-redirect-page" />
      <ion-route url="/menzis" component="sw-menzis-signup-page" />
      <ion-route url="/vinkvink" component="sw-menzis-signup-page" />
      <ion-route url="/anderzorg" component="sw-menzis-signup-page" />
      <ion-route
        url="/delraybeach"
        component="sw-eligibility-file-signup-page"
      />
      {/* todo: dynamically validate these eligibility file routes, probably by adding a new path (redirect the old ones for backward compatibility ) */}
      <ion-route
        url="/thewellnessloop"
        component="sw-eligibility-file-signup-page"
      />
      <ion-route url="/signant" component="sw-eligibility-file-signup-page" />
      <ion-route
        url="/signup-page"
        component="sw-eligibility-file-signup-page"
      />
      <ion-route
        url="/signup/:orgIdOrLandingPagePath"
        component="sw-eligibility-file-signup-page"
      />
      {/* todo: clean up saml handling with native apps, automate  */}
      <ion-route url="/saml/:providerId" component="sw-saml-login" />
      <ion-route url="/doctors-of-bc" component="sw-saml-login" />
      <ion-route url="/mecklenburg-county" component="sw-saml-login" />
      <ion-route url="/vp" component="sw-virgin-pulse-login-page" />
      <ion-route url="/auth/vp" component="sw-virgin-pulse-login-page" />
      <ion-route url="/virgin-pulse" component="sw-virgin-pulse-login-page" />
      <ion-route url="/auth/personify" component="sw-personify-login-page" />
      <ion-route url="/solera" component="sw-solera-login-page" />
      <ion-route url="/auth/solera" component="sw-solera-login-page" />
      <ion-route url="/auth/:orgIdOrLandingPagePath" component="sw-user-token-auth-page" />
      <ion-route url="/partners/:partnerCode" component="sw-partner-page" />
      <ion-route url="/invite/:inviteId" component="sw-consume-invite-page" />
      <ion-route url="/welcome" component="sw-welcome-page" />
      <ion-route url="/reports" component="sw-admin-report-page" />

      <ion-route
        url="/onboarding"
        component="sw-onboarding-primary-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/profile"
        component="sw-onboarding-primary-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/primary"
        component="sw-onboarding-primary-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/secondary/:onboardingChoice"
        component="sw-onboarding-secondary-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/kids"
        component="sw-kids-intent-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/activity-level"
        component="sw-recommendation-activity-level-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/interest"
        component="sw-recommendation-interest-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/injuries"
        component="sw-recommendation-injuries-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/pain"
        component="sw-recommendation-pain-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/gender"
        component="sw-recommendation-gender-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/age"
        component="sw-recommendation-age-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/weight"
        component="sw-recommendation-weight-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/connect-devices"
        component="sw-connect-devices-page"
        componentProps={{
          onboardingFlow: true
        }}
      />
      <ion-route
        url="/onboarding/struggle"
        component="sw-recommendation-struggle-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/physical-therapy"
        component="sw-recommendation-pt-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/goal"
        component="sw-recommendation-goal-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route
        url="/onboarding/push"
        component="sw-recommendation-push-page"
      />
      <ion-route
        url="/onboarding/recommended-plan"
        component="sw-recommendation-collection-page"
        componentProps={{
          onboardingFlow: true,
        }}
      />
      <ion-route url="/style-guide" component="sw-style-guide" />
      <ion-route url="/share/:sharedWorkoutId" component="sw-share-page" />
      <ion-route
        url="/share/:sharedWorkoutId/sign-in"
        component="sw-share-page"
        componentProps={{
          signIn: true,
        }}
      />
      {handleRedirects(
        authLoaded,
        isAuthenticated,
        loyalUser,
        oAuthSessionInitiated,
      )}
      {defaultRedirects()}
      {loyalUser ? (
        <ion-route url="/legacy" component="sw-legacy-page" />
      ) : null}
    </ion-router>
  );
}

const handleRedirects = (
  authLoaded: boolean,
  isAuthenticated: boolean,
  loyalUser: boolean,
  oauthSessionInitiated?: AuthState["oAuthInitiated"],
) => {
  if (authLoaded) {
    if (isAuthenticated) {
      // auth loaded & authenticated
      return directToApp(loyalUser);
    } else {
      if (oauthSessionInitiated) {
        if (Capacitor.isNativePlatform()) {
        } else {
          return kickOut(
            `/${oauthSessionInitiated}?redirected_from_login=true`,
          );
        }
      }
      // auth loaded not authenticated
      return kickOut("/welcome");
    }
  } else {
    // don't redirect
    return null;
  }
};

function defaultRedirects() {
  // console.debug('default redirect');
  return [
    <ion-route-redirect from="/workout/*" to="/" />,
    <ion-route-redirect from="/workouts" to="/" />,
    <ion-route-redirect from="/premium" to="/" />,
    <ion-route-redirect from="/plans" to="/" />,
  ];
}

function directToApp(loyalUser: boolean) {
  // console.debug('send in app');
  return [
    <ion-route-redirect from="/clever" to="/" />,
    <ion-route-redirect from="/saml" to="/" />,
    <ion-route-redirect from="/doctors-of-bc" to="/" />,
    <ion-route-redirect from="/mecklenburg-county" to="/" />,
    <ion-route-redirect from="/auth/vp" to="/" />,
    <ion-route-redirect from="/auth/personify" to="/" />,
    <ion-route-redirect from="/vp" to="/" />,
    <ion-route-redirect from="/virgin-pulse" to="/" />,
    <ion-route-redirect from="/welcome" to="/" />,
    // loyalUsers see loyal thanks
    <ion-route-redirect from="/login" to={loyalUser ? "/legacy" : "/"} />,
    loyalUser ? null : <ion-route-redirect from="/legacy" to="/" />,
  ];
}

function kickOut(URL: string) {
  // console.debug('kick out of app to ', URL);
  return [
    <ion-route-redirect from="/" to={URL} />,
    <ion-route-redirect from="/collections" to={URL} />,
    <ion-route-redirect from="/goals" to={URL} />,
    <ion-route-redirect from="/goals/*" to={URL} />,
    <ion-route-redirect from="/custom" to={URL} />,
    <ion-route-redirect from="/settings" to={URL} />,
    <ion-route-redirect from="/my-sworkit" to={URL} />,
    <ion-route-redirect from="/my-sworkit/*" to={URL} />,
    <ion-route-redirect from="/premium" to={URL} />,
    <ion-route-redirect from="/legacy" to={URL} />,
    // don't redirect from login to welcome
    URL === "/welcome" ? null : <ion-route-redirect from="/login" to={URL} />,
  ];
}

const routesThatRequireLogin: { component: string; url: string }[] = [
  { component: "sw-referral-code-page", url: "/redeem" },
  { component: "sw-referral-code-page", url: "/redeem/:referralCode" },
  { component: "sw-subscribe-page", url: "/subscribe/cancel" },
  { component: "sw-subscribe-success-page", url: "/subscribe/success" },
  {
    component: "sw-subscribe-success-page",
    url: "/subscribe/success/:stripeSessionId",
  },
  { component: "sw-subscribe-page", url: "/coupon" },
  { component: "sw-subscribe-page", url: "/coupon/:couponId" },
  { component: "sw-activate-apple-tv-page", url: "/apple-tv" },
  { component: "sw-activate-apple-tv-page", url: "/activate" },
  {
    component: "sw-add-workout-log-page",
    url: "/group-workout/:workoutLogId",
  },
  {
    url: "/account",
    component: "sw-account-page",
  },
  {
    url: "/account/profile",
    component: "sw-edit-profile-page",
  },
  {
    url: "/account/profile/email",
    component: "sw-edit-email-page",
  },
  {
    url: "/account/profile/update",
    component: "sw-change-password-page",
  },
  {
    url: "/account/subscription",
    component: "sw-manage-subscription-page",
  },
  { url: "/challenges/:challengeId", component: "sw-challenge-page" },
];

const routesThatDontRequireLogin: { component: string; url: string }[] = [
  {
    component: "sw-exercise-preview-page",
    url: "/exercise-library/:exerciseSlug",
  },
  { component: "sw-promo-page", url: "/promo/:promoId" },
  { component: "sw-exercise-library-page", url: "/exercise-library" },
  { component: "sw-exercise-filter-page", url: "/exercise-library/filter" },
];

function getPortalRoutes(isAuthenticated: boolean) {
  if (!isAuthenticated) return null;
  return (
    <Fragment>
      <ion-route component="sw-portal" url="/organization/:orgId">
        <ion-route url="/" component="sw-organization-details-page" />
        <ion-route
          url="/manage-members"
          component="sw-organization-members-page"
        />
        <ion-route
          url="/manage-members/eligibility-file"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "ELIGIBILITY_FILE" }}
        />
        <ion-route
          url="/manage-members/invites"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "INVITES" }}
        />
        <ion-route
          url="/manage-members/all"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "MEMBERS" }}
        />
        <ion-route
          url="/manage-members/admins"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "ADMINS" }}
        />
        <ion-route
          url="/missing-permission"
          component="missing-permission-page"
        />
        <ion-route url="/dashboard" component="portal-dashboard-page" />

        <ion-route url="/usage" component="sw-usage-page" />
        <ion-route url="/members-report" component="sw-members-report-page" />
        <ion-route
          url="/members-report/:uid"
          component="sw-member-report-page"
        />
        <ion-route url="/groups" component="sw-groups-page" />
        <ion-route url="/groups/:groupId" component="sw-group-report-page" />
        <ion-route url="/assignments" component="sw-assignments-page" />

        <ion-route url="/challenges" component="sw-view-org-challenges-page" />
        <ion-route
          url="/integrations"
          component="portal-integrations-page"
        />

        <ion-route
          url="integrations/awardco"
          component="portal-integration-awardco-page"
        />

        <ion-route
          url="integrations/achievers"
          component="achievers-integration-page"
        />

        <ion-route url="/curriculum" component="curriculum-page" />

        <ion-route
          url="/challenges/:challengeId"
          component="sw-challenge-page"
          componentProps={{ orgView: true }}
        />
        <ion-route
          url="/challenge-builder"
          component="sw-edit-challenge-page"
        />
        <ion-route
          url="/challenge-builder/details"
          component="sw-edit-challenge-details-page"
        />
        <ion-route
          url="/challenge-builder/dates"
          component="sw-edit-challenge-dates-page"
        />

        <ion-route
          url="/challenge-builder/members"
          component="sw-edit-challenge-members-page"
        />
        <ion-route
          url="/challenge-builder/goals"
          component="sw-edit-challenge-goals-page"
        />
        <ion-route
          url="/challenge-builder/settings"
          component="sw-edit-challenge-settings-page"
        />
        <ion-route
          url="/challenge-builder/preview"
          component="sw-edit-challenge-preview-page"
        />
        <ion-route
          url="/challenge-builder/intercom-tags"
          component="sw-edit-challenge-intercom-tags-page"
        />
        <ion-route
          url="/challenge-builder/workouts"
          component="sw-edit-challenge-workouts-page"
        />
      </ion-route>

      <ion-route url="/organization" component="sw-portal"></ion-route>
    </Fragment>
  );
}

function getCurriculumOnlyPortalRoutes(isAuthenticated: boolean) {
  if (!isAuthenticated) return null;
  return (
    <Fragment>
      <ion-route component="sw-portal" url="/organization/:orgId">
        <ion-route
          url="/"
          componentProps={{
            routeName: "details",
            showCurriculumOnlyModal: true,
          }}
          component="curriculum-page"
        />
        <ion-route
          url="/manage-members"
          component="sw-organization-members-page"
        />
        <ion-route
          url="/manage-members/eligibility-file"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "ELIGIBILITY_FILE" }}
        />
        <ion-route
          url="/manage-members/invites"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "INVITES" }}
        />
        <ion-route
          url="/manage-members/all"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "MEMBERS" }}
        />
        <ion-route
          url="/manage-members/admins"
          component="sw-member-management-page"
          componentProps={{ activeSegment: "ADMINS" }}
        />
        <ion-route
          url="/missing-permission"
          component="sw-missing-permission-page"
        />
        <ion-route
          url="/usage"
          componentProps={{ routeName: 'usage', showCurriculumOnlyModal: true }}
          component="curriculum-page"
        />
        <ion-route
          url="/members-report"
          componentProps={{
            routeName: "members-report",
            showCurriculumOnlyModal: true,
          }}
          component="curriculum-page"
        />
        <ion-route
          url="/members-report/:uid"
          componentProps={{
            routeName: "report",
            showCurriculumOnlyModal: true,
          }}
          component="curriculum-page"
        />
        <ion-route
          url="/groups"
          componentProps={{
            routeName: "groups",
            showCurriculumOnlyModal: true,
          }}
          component="curriculum-page"
        />
        <ion-route
          url="/groups/:groupId"
          componentProps={{
            routeName: "groupsId",
            showCurriculumOnlyModal: true,
          }}
          component="curriculum-page"
        />
        <ion-route
          url="/challenges"
          componentProps={{
            routeName: "challenges",
            showCurriculumOnlyModal: true,
          }}
          component="curriculum-page"
        />
        <ion-route url="/curriculum" component="curriculum-page" />
        <ion-route
          url="/challenges/:challengeId"
          componentProps={{
            routeName: "challengesId",
            showCurriculumOnlyModal: true,
          }}
          component="curriculum-page"
        />
      </ion-route>
      <ion-route url="/organization" component="sw-portal"></ion-route>
    </Fragment>
  );
}